import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import DeckleImage from '@/components/generals/deckle-image'
import Reveal from '@/components/generals/reveal'
import { ghostButtonOnDark } from '@/components/generals/button-styles'
import { formatPrice } from '@/lib/format'
import { WORKSHOP } from '@/constants/content'

const FACTS: Array<{ label: string; value: string }> = [
    { label: 'Duration', value: `${WORKSHOP.duration}, from ${WORKSHOP.start}` },
    { label: 'Price', value: `${formatPrice(WORKSHOP.pricePerPerson)} per person` },
    { label: 'Group', value: `Up to ${WORKSHOP.maxGuests} guests` },
    { label: 'Days', value: WORKSHOP.days }
]

const WorkshopInvite: FC = (): ReactNode => {
    return (
        <section id='workshop' aria-labelledby='workshop-title' className='relative py-4'>
            <div className='torn-edges bg-indigo text-paper'>
                <div className='container-page grid items-center gap-14 py-24 md:py-32 lg:grid-cols-12 lg:gap-8'>
                    <Reveal className='lg:col-span-5'>
                        <p className='flex items-center gap-4 text-sm font-medium text-paper/75'>
                            <span aria-hidden='true' className='h-px w-10 bg-paper/50' />
                            Half-day workshops
                        </p>
                        <h2 id='workshop-title' className='mt-6 font-heading text-[clamp(2.3rem,4.6vw,4rem)] font-light leading-[1.04] tracking-[-0.02em]'>
                            Spend a morning at the mill, leave with paper you made.
                        </h2>
                        <p className='mt-7 max-w-lg text-[1.05rem] leading-relaxed text-paper/80'>
                            Strip mulberry bark, stand beside the water hammer, lift your own sheets from the vat and polish one with an agate stone. Four unhurried hours in our courtyard, with tea, lunch and a fair amount of splashing.
                        </p>

                        <dl className='mt-10 grid grid-cols-2 gap-x-6 gap-y-6 border-t border-paper/20 pt-8'>
                            {FACTS.map(fact => (
                                <div key={fact.label}>
                                    <dt className='text-sm text-paper/70'>{fact.label}</dt>
                                    <dd className='mt-1.5 font-heading text-[1.2rem] leading-snug'>{fact.value}</dd>
                                </div>
                            ))}
                        </dl>

                        <div className='mt-10 flex flex-wrap gap-3'>
                            <Link
                                href='/workshops#book'
                                className={cn(
                                    'inline-flex items-center justify-center gap-3 rounded-[3px] bg-paper px-7 py-3.5 text-[0.95rem] font-medium text-bark',
                                    'transition-colors duration-500 hover:bg-paper-deep focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-paper'
                                )}
                            >
                                Book your morning
                                <ArrowRight className='size-4' strokeWidth={1.75} />
                            </Link>
                            <Link href='/workshops' className={ghostButtonOnDark}>
                                What happens on the day
                            </Link>
                        </div>
                    </Reveal>

                    <div className='relative lg:col-span-6 lg:col-start-7'>
                        <Reveal delay={150}>
                            <div className='lg:-rotate-[1deg]'>
                                <DeckleImage
                                    src='/images/qogoz/workshop-visitors.jpg'
                                    alt='Visitors forming paper at a wooden vat in a sunlit courtyard, guided by a papermaker in an indigo apron'
                                    sizes='(min-width: 1024px) 46vw, 92vw'
                                    shape='landscape'
                                />
                            </div>
                        </Reveal>
                        <Reveal delay={400} className='relative -mt-10 ml-auto mr-4 w-[min(17rem,72%)] sm:mr-10'>
                            <div className='rotate-[2deg] bg-paper px-5 py-4 text-bark shadow-[0_18px_30px_-18px_rgba(0,0,0,0.55)]'>
                                <p className='font-heading text-[1.05rem] leading-snug'>
                                    Your sheets dry overnight. We bring them to your hotel the next morning.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default WorkshopInvite
